import * as _ from "lodash-es";
import { useEffect, useRef, useState } from "react";
import PaginationDirectionEnum from "../../../models/PaginationDirectionEnum";
import "./PaginationBar.scss";
import OwInputWrapper from "../OwInputWrapper/OwInputWrapper";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import { PaginationBarProps } from "./PaginationBarProps";
import { debounce } from "lodash-es";
import OwButton from "../OwButton/OwButton";
import TokenService from "@services/TokenService";
import { useAppContextNonNull } from "@contexts/AppContext";
import ITokenSettings from "@models/ITokenSettings";
import NotificationLevelEnum from "@models/NotificationLevelEnum";

type Props = PaginationBarProps;

export const PaginationBar = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const createOffsetArr = () => {
        const offset = currentOffset;
        const limit = currentLimit;
        const totalRecords = props.totalRecords;

        const startIndex = 1;
        const endIndex = Math.ceil(totalRecords / limit);
        const _currentOffset = offset;

        const offsetArr: { value: number; isSelected: boolean }[] = [];

        for (let i = startIndex; i <= endIndex; ++i) {
            const detail = {
                value: i,
                isSelected: false,
            };

            if (detail.value === _currentOffset + 1) {
                detail.isSelected = true;
            }

            offsetArr.push(detail);
        }

        return offsetArr;
    };

    const getTotalPageCount = () => {
        const limit = currentLimit;
        if (props.totalRecords === 0) {
            return 1;
        }
        return Math.ceil(props.totalRecords / limit);
    };

    const createLimitArr = () => {
        let limitArr = [
            { value: 5, isSelected: false },
            { value: 10, isSelected: false },
            { value: 25, isSelected: false },
            { value: 50, isSelected: false },
            { value: 100, isSelected: false },
            { value: 250, isSelected: false },
            { value: 500, isSelected: false },
        ];

        if (props.limitArr) {
            limitArr = props.limitArr;
        }

        const _currentLimit = currentLimit || 5;

        for (let i = 0; i < limitArr.length; ++i) {
            const limit = limitArr[i];

            if (limit.value === _currentLimit) {
                limitArr[i].isSelected = true;
            }
        }

        return limitArr;
    };

    const [currentLimit, setCurrentLimit] = useState(props.limit || 25);
    const [currentOffset, setCurrentOffset] = useState(props.offset || 0);
    const [offsetArr, setOffsetArr] = useState(createOffsetArr());
    const [limitArr, setLimitArr] = useState(createLimitArr());
    const [totalPageCount, setTotalPageCount] = useState(getTotalPageCount());

    const [tokenLimit, setTokenLimit] = useState(props.tokenLimit || 25);

    const isInitialMount = useRef(true);

    useEffect(() => {
        setCurrentLimit(props.limit || 25);
        setCurrentOffset(props.offset || 0);
        setOffsetArr(createOffsetArr());
        setLimitArr(createLimitArr());

        setTotalPageCount(getTotalPageCount());
    }, [props.limit, props.offset, props.totalRecords]);

    useEffect(() => {
        setTotalPageCount(getTotalPageCount());

        // Skip the initial mount - parent component already fetches on mount
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        props.onPaginationChanged(currentOffset, currentLimit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLimit, currentOffset]);

    const onPaginationChangedDebounced = () => {
        debounce((e: any) => {
            onPaginationChanged();
        }, 1000);
    };

    const setOffsetDetails = (selectedOffset: number) => {
        setCurrentOffset(selectedOffset - 1);
        setOffsetArr(createOffsetArr());
        setTotalPageCount(getTotalPageCount());
    };

    const setLimitDetails = (selectedLimit: number) => {
        setCurrentLimit(selectedLimit);
        setCurrentOffset(0);
        setLimitArr(createLimitArr());
        setOffsetArr(createOffsetArr());
        setTotalPageCount(getTotalPageCount());
    };

    const onPaginationChanged = () => {
        const offset = currentOffset;
        const limit = currentLimit;

        props.onPaginationChanged(offset, limit);
    };

    const offsetSetup = () => {
        const offsetSetup = _.map(offsetArr, (offset, index) => {
            return (
                <a
                    className={"me-5 pointer text-primary " + (offset.isSelected ? "font-weight-bold offset-selected" : "")}
                    key={index}
                    onClick={() => {
                        setOffsetDetails(offset.value);
                    }}>
                    {offset.value}
                </a>
            );
        });

        return offsetSetup;
    };

    const enterFn = (e: any) => {
        if (e.key === "Enter") {
            onPaginationChanged();
        }
    };

    const handleOffsetChange = (e: any) => {
        const value = e.target.value;

        const totalPageCount = getTotalPageCount();

        if (value > 0 && value <= totalPageCount) {
            setCurrentOffset(value - 1);
            onPaginationChangedDebounced();
        }
    };

    const movePagination = (direction: PaginationDirectionEnum) => {
        let offset = currentOffset;

        switch (direction) {
            case PaginationDirectionEnum.DECR:
                offset = offset - 1;
                break;

            case PaginationDirectionEnum.INCR:
                offset = offset + 1;
                break;

            case PaginationDirectionEnum.START:
                offset = 0;
                break;

            case PaginationDirectionEnum.END:
                offset = Math.max(0, totalPageCount! - 1);
                break;
        }

        if (offset <= 0) {
            offset = 0;
        }

        if (offset >= totalPageCount!) {
            offset = totalPageCount! - 1;
        }

        setCurrentOffset(offset);
    };

    // const offsetDOM = this.offsetSetup();

    const currentPage = currentOffset + 1;

    // const totalPageCount = getTotalPageCount();

    let disableDecrJump = currentOffset === 0;
    let disableIncrJump = totalPageCount! <= 1 || currentOffset >= totalPageCount! - 1;

    if (props.isActive) {
        disableDecrJump = true;
        disableIncrJump = true;
    }

    let limitCss = "col-12 col-md-2 d-inline-flex d-print-none mb-2 mb-md-0";

    if (props.limitCss) {
        limitCss = props.limitCss;
    }

    return (
        <div data-testid={"pagination-bar"} className="PaginationBar mb-3 position-relative">
            <div className="flex-column w-100 flex-wrap justify-content-center align-items-center">
                {props.hideLimit ? null : (
                    <div className={limitCss} style={{ zIndex: 1 }}>
                        <OwSelectOption
                            isActive={props.isActive}
                            isDisabledOnActive={true}
                            valueProp={"value"}
                            name={"limit"}
                            titleProp={"value"}
                            selectedOption={currentLimit}
                            hasSelectedOptionProp={false}
                            options={limitArr}
                            fn={(e: any) => {
                                setLimitDetails(Number(e.target.value));
                            }}
                        />
                        {tokenLimit && currentLimit !== tokenLimit && props.tokenGuid && (
                            <div className="ms-2">
                                <OwButton
                                    title={"save"}
                                    fn={() => {
                                        if (props.tokenGuid && props.tokenLimitProp) {
                                            TokenService.updateTokenSettings(
                                                props.tokenGuid,
                                                {
                                                    [props.tokenLimitProp as keyof ITokenSettings]: currentLimit,
                                                },
                                                userModel,
                                                supplierModel
                                            ).then(() => {
                                                uiContext.showNotification({
                                                    message: "successfully_updated_settings",
                                                    level: NotificationLevelEnum.success,
                                                });
                                                setTokenLimit(currentLimit);
                                            });
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
                <div
                    className={
                        "col-12 col-md-10 justify-content-between " +
                        (props.centered ? "justify-content-md-center" : "justify-content-md-end") +
                        " d-inline-flex pagination-wrapper"
                    }>
                    <div
                        data-testid={"pagination-range"}
                        className={
                            "d-flex justify-content-between " +
                            (props.centered ? "justify-content-md-center" : "justify-content-md-end") +
                            " align-items-center flex-grow-1 flex-grow-md-0"
                        }>
                        <div
                            data-testid={"pagination-start"}
                            className={"me-md-4 pagination-left d-print-none " + (disableDecrJump ? "disabled" : "")}
                            onClick={() => {
                                if (!disableDecrJump) {
                                    movePagination(PaginationDirectionEnum.START);
                                }
                            }}>
                            <i className={"fas fa-fast-backward"} />
                        </div>
                        <div
                            data-testid={"pagination-prev"}
                            className={"me-md-4 pagination-left d-print-none " + (disableDecrJump ? "disabled" : "")}
                            onClick={() => {
                                if (!disableDecrJump) {
                                    movePagination(PaginationDirectionEnum.DECR);
                                }
                            }}>
                            <i className={"fas fa-play fa-rotate-180 fa-xs"} />
                        </div>
                        <div className={"me-md-4 w-4r"}>
                            <div>
                                <OwInputWrapper
                                    disabled={props.isActive}
                                    name={"offset"}
                                    type={"number"}
                                    className={"text-center w-3r"}
                                    min={1}
                                    max={totalPageCount}
                                    fn={handleOffsetChange}
                                    value={currentPage}
                                    onKeyDownFn={enterFn}
                                />
                            </div>
                        </div>
                        {/*{offsetDOM}*/}
                        <div className={"me-md-4"}>of</div>
                        <div className={"me-md-4"}>{totalPageCount}</div>

                        <div
                            data-testid={"pagination-next"}
                            className={"me-md-4 pagination-right d-print-none " + (disableIncrJump ? "disabled" : "")}
                            onClick={() => {
                                if (!disableIncrJump) {
                                    movePagination(PaginationDirectionEnum.INCR);
                                }
                            }}>
                            <i className={"fas fa-play fa-xs"} />
                        </div>
                        <div
                            data-testid={"pagination-end"}
                            className={"pagination-right d-print-none " + (disableIncrJump ? "disabled" : "")}
                            onClick={() => {
                                if (!disableIncrJump) {
                                    movePagination(PaginationDirectionEnum.END);
                                }
                            }}>
                            <i className={"fas fa-fast-forward"} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaginationBar;
